import React, { useState } from 'react';
import { Gift, Sparkles, Loader2 } from 'lucide-react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useProgram } from '../hooks/useProgram';
import { createUserStakeAccountInstruction, derivePoolPda, deriveUserStakePda } from '../utils/instructions';
import { sendTx } from '../utils/transactions';
import { createAssociatedTokenAccountInstruction, getAssociatedTokenAddress } from '@solana/spl-token';
import { PublicKey } from '@solana/web3.js';
import { ROLL_MINT } from '../constants';
import GradientButton from './GradientButton';
import StakingCard from './StakingCard';
import { useModal } from '../hooks/useFeedbackModal';

const OnboardingSteps = ({ 
  onCreateStakingAccount, 
  onInitializeRollWallet,
  isStakingAccountCreated,
  isRollWalletInitialized 
}) => {
  const [isCreatingStakeAccount, setIsCreatingStakeAccount] = useState(false);
  const [isCreatingRollAccount, setIsCreatingRollAccount] = useState(false);
  const { publicKey, signTransaction } = useWallet();
  const { connection, monopolyMint, rollMint } = useProgram();
  const modal = useModal();
  
  const currentStep = !isStakingAccountCreated ? 1 : !isRollWalletInitialized ? 2 : 3;

  const handleCreateStakingAccount = async () => {
    if (!publicKey || !signTransaction) {
      modal.showModal({
        title: 'Connection Error',
        description: 'Please connect your wallet to continue.',
        type: 'error'
      });
      return;
    }

    setIsCreatingStakeAccount(true);
    try {
      const [poolPda] = derivePoolPda(monopolyMint, rollMint);
      const [userStakePda] = await deriveUserStakePda(poolPda, publicKey);

      const ix = createUserStakeAccountInstruction(
        publicKey,
        poolPda,
        userStakePda,
      );

      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      
      modal.showModal({
        title: 'Success',
        description: 'Staking account created successfully!',
        type: 'success'
      });
      
      onCreateStakingAccount();
    } catch (err) {
      console.error('Failed to create staking account:', err);
      modal.showModal({
        title: 'Error',
        description: 'Failed to create staking account. Please try again.',
        type: 'error'
      });
    } finally {
      setIsCreatingStakeAccount(false);
    }
  };

  const handleInitializeRollWallet = async () => {
    if (!publicKey || !signTransaction) {
      modal.showModal({
        title: 'Connection Error',
        description: 'Please connect your wallet to continue.',
        type: 'error'
      });
      return;
    }

    setIsCreatingRollAccount(true);
    try {
      const userRollAccount = await getAssociatedTokenAddress(
        new PublicKey(ROLL_MINT),
        publicKey
      );

      const ix = createAssociatedTokenAccountInstruction(
        publicKey,
        userRollAccount,
        publicKey,
        new PublicKey(ROLL_MINT)
      );

      await sendTx(connection, { publicKey, signTransaction }, [ix]);
      
      modal.showModal({
        title: 'Success',
        description: 'ROLL token account created successfully!',
        type: 'success'
      });
      
      onInitializeRollWallet();
    } catch (err) {
      console.error('Failed to create ROLL account:', err);
      modal.showModal({
        title: 'Error',
        description: 'Failed to create ROLL account. Please try again.',
        type: 'error'
      });
    } finally {
      setIsCreatingRollAccount(false);
    }
  };
  
  return (
    <StakingCard>
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-pink-600">
            Complete Setup to Start Staking
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Follow these steps to begin earning ROLL rewards
          </p>
        </div>

        {/* Progress Bar */}
        <div className="relative">
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
            <div 
              className="absolute left-0 h-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${((currentStep - 1) / 2) * 100}%` }}
            />
          </div>
          <div className="mt-2 flex justify-between text-sm text-gray-500">
            <span>Start</span>
            <span>Ready to Stake</span>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-6">
          {/* Step 1: Create Staking Position */}
          <div className={`p-6 rounded-xl border-2 transition-all duration-300 ${
            currentStep === 1 
              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/10' 
              : currentStep > 1
                ? 'border-green-500 bg-green-50 dark:bg-green-900/10'
                : 'border-gray-200 dark:border-gray-700'
          }`}>
            <div className="flex justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  currentStep === 1 
                    ? 'bg-purple-500' 
                    : currentStep > 1
                      ? 'bg-green-500'
                      : 'bg-gray-200 dark:bg-gray-700'
                }`}>
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">Create Staking Position</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Set up your staking account
                  </p>
                </div>
              </div>
              {currentStep === 1 && (
                <GradientButton 
                  onClick={handleCreateStakingAccount}
                  disabled={isCreatingStakeAccount}
                >
                  {isCreatingStakeAccount ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </div>
                  ) : (
                    'Create Account'
                  )}
                </GradientButton>
              )}
            </div>
          </div>

          {/* Step 2: Initialize ROLL Account */}
          <div className={`p-6 rounded-xl border-2 transition-all duration-300 ${
            currentStep === 2
              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/10'
              : currentStep > 2
                ? 'border-green-500 bg-green-50 dark:bg-green-900/10'
                : 'border-gray-200 dark:border-gray-700'
          }`}>
            <div className="flex justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  currentStep === 2
                    ? 'bg-purple-500'
                    : currentStep > 2
                      ? 'bg-green-500'
                      : 'bg-gray-200 dark:bg-gray-700'
                }`}>
                  <Gift className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">Create ROLL Account</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Initialize your rewards account
                  </p>
                </div>
              </div>
              {currentStep === 2 && (
                <GradientButton 
                  onClick={handleInitializeRollWallet}
                  disabled={isCreatingRollAccount}
                >
                  {isCreatingRollAccount ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </div>
                  ) : (
                    'Create Account'
                  )}
                </GradientButton>
              )}
            </div>
          </div>

          {/* All Steps Complete Message */}
          {currentStep === 3 && (
            <div className="text-center p-4 bg-green-50 dark:bg-green-900/10 rounded-xl">
              <p className="text-green-600 dark:text-green-400 font-medium">
                🎉 Setup complete! You're ready to start staking.
              </p>
            </div>
          )}
        </div>
      </div>
    </StakingCard>
  );
};

export default OnboardingSteps;
